import { normalizePickupId } from './idHelpers';
import { flattenTrailerSpottedFields } from './trailerNormalization';
import { TRAILER_UPDATE_KEYS } from './swapBoardPayloads';
import type {
  SwapBoardGroup,
  SwapBoardTrailer,
  SwapBoardUpdatePayload,
  SwapBoardUpdateResult,
} from './types';

const TRAILER_UPSERT_OMIT_KEYS = new Set([
  'pickupIds',
  'pickupId',
  'rmaxShipperCode',
  'shipperName',
  'spottedTrailers',
  'success',
  'eventType',
  'data',
  'action',
  'showNotification',
]);

const findTrailerInGroups = (groups: SwapBoardGroup[], pickupId: string) => {
  const normalizedPickupId = normalizePickupId(pickupId);
  return (
    groups
      .flatMap((group) =>
        (group.trailers || [])
          .filter((trailer) => normalizePickupId(trailer.pickupId) === normalizedPickupId)
          .map((trailer) => ({ group, trailer }))
      )[0] ?? null
  );
};

const buildTrailerFromPayload = (payload: SwapBoardUpdatePayload, pickupId: string) =>
  flattenTrailerSpottedFields(
    Object.entries(payload).reduce<SwapBoardTrailer>(
      (trailer, [key, value]) => {
        if (!TRAILER_UPSERT_OMIT_KEYS.has(key) && value !== undefined) {
          trailer[key] = value;
        }
        return trailer;
      },
      { pickupId }
    )
  );

export const mergeTrailerFromUpdate = (
  trailer: SwapBoardTrailer,
  payload: SwapBoardUpdatePayload
) => {
  const updates = TRAILER_UPDATE_KEYS.reduce<Partial<SwapBoardTrailer>>((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      acc[key] = payload[key];
    }
    return acc;
  }, {});

  return Object.keys(updates).length
    ? flattenTrailerSpottedFields({ ...trailer, ...updates })
    : trailer;
};

export const applyGroupUpdateFields = (
  group: SwapBoardGroup,
  payload: SwapBoardUpdatePayload
) => {
  if (payload.spottedTrailers === undefined) {
    return group;
  }
  return { ...group, spottedTrailers: payload.spottedTrailers };
};

export type UpsertTrailersFromPayloadParams = {
  apiGroups: SwapBoardGroup[];
  payload: SwapBoardUpdatePayload;
  pickupIds: string[];
};

export const upsertTrailersFromPayload = ({
  apiGroups,
  payload,
  pickupIds,
}: UpsertTrailersFromPayloadParams): SwapBoardUpdateResult => {
  if (!Array.isArray(apiGroups) || !pickupIds.length) {
    return { groups: apiGroups, updated: false };
  }

  const normalizedShipperCode = payload.rmaxShipperCode?.toUpperCase?.();
  if (!normalizedShipperCode) {
    return { groups: apiGroups, updated: false };
  }

  const missingPickupIds = pickupIds.filter(
    (pickupId) => !findTrailerInGroups(apiGroups, pickupId)
  );

  if (!missingPickupIds.length) {
    return { groups: apiGroups, updated: false };
  }

  const existingGroupIndex = apiGroups.findIndex(
    (group) => group.rmaxShipperCode?.toUpperCase?.() === normalizedShipperCode
  );

  if (existingGroupIndex >= 0) {
    const existingGroup = apiGroups[existingGroupIndex];
    const nextTrailers = [
      ...(existingGroup.trailers || []),
      ...missingPickupIds
        .map((pickupId) => buildTrailerFromPayload(payload, pickupId))
        .filter((trailer): trailer is SwapBoardTrailer => Boolean(trailer)),
    ];

    return {
      groups: apiGroups.map((group, index) =>
        index === existingGroupIndex
          ? applyGroupUpdateFields({ ...group, trailers: nextTrailers }, payload)
          : group
      ),
      updated: true,
    };
  }

  return {
    groups: [
      ...apiGroups,
      applyGroupUpdateFields(
        {
          rmaxShipperCode: payload.rmaxShipperCode as string,
          shipperName: payload.shipperName ?? '',
          spottedTrailers: payload.spottedTrailers ?? 0,
          trailers: missingPickupIds
            .map((pickupId) => buildTrailerFromPayload(payload, pickupId))
            .filter((trailer): trailer is SwapBoardTrailer => Boolean(trailer)),
        },
        payload
      ),
    ],
    updated: true,
  };
};
